/**
 * THE MARKER VOCABULARY — five fills, and the one bridge from the wire.
 *
 * A professional state is WORDS. The marker is a 7px square that points at the
 * words; it never carries the meaning on its own, which is why there is no
 * coloured pill anywhere in this product. Five fills is the whole vocabulary,
 * and it is closed: a sixth state would be a professional distinction the
 * record cannot make.
 *
 * The server sends its own tone vocabulary (contracts/work.ts `Tone`), because
 * tone is a conclusion about the work and conclusions are decided where the
 * evidence is. This file holds the ONLY translation between the two. It is
 * exhaustive by type: add a tone to the contract and this stops compiling,
 * which is the point — a tone with no marker would otherwise render as
 * whatever the fallback happened to be.
 */
import type { Tone } from "@leveri/contracts";

/** The five fills. `optional` is the absence of a state, never a quiet success. */
export type MarkerTone = "established" | "attention" | "conflict" | "progress" | "optional";

/**
 * Fill classes, and note what is NOT here: no text colour. Three of these
 * values (`est`, `att`, `optional`) fail contrast at body size and are marked
 * FILL ONLY in the sheet. Colouring words with them is a defect the token test
 * catches; the `-text` variants exist for words.
 */
export const MARKER_FILL: Record<MarkerTone, string> = {
  established: "bg-est",
  attention: "bg-att",
  conflict: "bg-con",
  progress: "bg-prog",
  optional: "bg-optional",
};

/**
 * Wire tone → marker. The two mappings worth stating out loud:
 *
 * `brand` → `progress`, not a success mark. WORKING and WAITING_FOR_EVIDENCE
 * are both toned brand, and waiting for evidence is the normal, expected state
 * of careful professional work — it is progress, not an alarm and not a result.
 *
 * `neutral` → `optional`, never established. An unknown or absent position must
 * never be able to render as a settled one; the failure this guards against is
 * a screen that looks finished because nobody decided anything.
 */
export function markerTone(tone: Tone): MarkerTone {
  switch (tone) {
    case "ok":
      return "established";
    case "warn":
      return "attention";
    case "danger":
      return "conflict";
    case "brand":
      return "progress";
    case "neutral":
      return "optional";
  }
}
